/**
 * useMaterialSuppliers — ViewModel for /manage/material-suppliers.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for reads; owns the add + delete mutations. List
 * call carries picker refs (active raw materials + active suppliers)
 * so the form's two dropdowns render without a second round-trip.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  MaterialSupplierLinkDto,
  RawMaterialRef,
  SupplierRef
} from '../models/material-supplier.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export type {
  MaterialSupplierLinkDto,
  RawMaterialRef,
  SupplierRef
} from '../models/material-supplier.model'

export interface CreateMaterialSupplierLinkInput {
  readonly raw_material_id: string
  readonly supplier_id: string
}

export interface UseMaterialSuppliersReturn {
  readonly items: MaterialSupplierLinkDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  /** Active raw materials for the form's material dropdown. */
  readonly rawMaterials: RawMaterialRef[]
  /** Active suppliers for the form's supplier dropdown (all types). */
  readonly suppliers: SupplierRef[]
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly add: (
    input: CreateMaterialSupplierLinkInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListExtras {
  readonly rawMaterials: RawMaterialRef[]
  readonly suppliers: SupplierRef[]
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: MaterialSupplierLinkDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly picker: { readonly rawMaterials: RawMaterialRef[]; readonly suppliers: SupplierRef[] }
  }
}

const EMPTY_EXTRAS: ListExtras = { rawMaterials: [], suppliers: [] }

export function useMaterialSuppliers(): UseMaterialSuppliersReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<MaterialSupplierLinkDto, ListExtras>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGEMATERIALSUPPLIERS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: {
            rawMaterials: body.data.picker.rawMaterials,
            suppliers: body.data.picker.suppliers
          }
        }
      },
      [authedFetch]
    )
  })

  const add = useCallback(
    async (input: CreateMaterialSupplierLinkInput) => {
      const r = await mutateJson<MaterialSupplierLinkDto>(
        authedFetch,
        APIROUTES.MANAGEMATERIALSUPPLIERS,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGEMATERIALSUPPLIERITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const extras = paged.extras ?? EMPTY_EXTRAS
  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    rawMaterials: extras.rawMaterials,
    suppliers: extras.suppliers,
    setPage: paged.setPage,
    reload: paged.reload,
    add,
    remove
  }
}
